import { Box, Heading, Stack, useColorMode } from '@chakra-ui/react';
import { PageContainer } from '../page-container';
import { Card } from '@ui/Card';
import { SectionHeading } from '../home/section-heading';
import { Support } from '../home/support';
import { DocsTemplateContent } from '@types';

type Props = {
  content: DocsTemplateContent;
};

export const SDKs = (
  isLightMode: boolean
): Array<{
  title: string;
  description: string;
  url: string;
  logoIcon: string;
  logoSize: 'sm' | 'md';
}> => [
  {
    title: 'React',
    description:
      'Clerk React is the recommended way to integrate Clerk into your React application.',
    url: '/docs/references/react/overview',
    logoIcon: '/images/logos/react.svg',
    logoSize: 'sm'
  },
  {
    title: 'JavaScript',
    description:
      'ClerkJS is our foundational JavaScript library for building user management and authentication.',
    url: '/docs/references/javascript/overview',
    logoIcon: '/images/logos/javascript.svg',
    logoSize: 'sm'
  },
  {
    title: 'Node.js',
    description:
      'Clerk Node SDK is the recommend way to integrate Clerk into your Node / Express application.',
    url: '/docs/references/nodejs/overview',
    logoIcon: isLightMode
      ? '/images/logos/node.svg'
      : '/images/logos/node-white.svg',
    logoSize: 'md'
  },
  {
    title: 'React Native / Expo',
    description:
      'Clerk Expo is the recommended way to integrate Clerk into your React Native application.',
    url: '/docs/quickstarts/expo',
    logoIcon: isLightMode
      ? '/images/logos/expo.svg'
      : '/images/logos/expo-white.svg',
    logoSize: 'sm'
  },
  {
    title: 'Go',
    description:
      'Clerk Go SDK is a wrapper around our Backend API written in Golang.',
    url: '/docs/references/go/overview',
    logoIcon: '/images/logos/go.svg',
    logoSize: 'md'
  },
  {
    title: 'Ruby',
    description:
      'Clerk Ruby is the recommended way to integrate Clerk into your Ruby application.',
    url: '/docs/references/ruby/overview',
    logoIcon: '/images/logos/ruby.svg',
    logoSize: 'sm'
  }
];

export function DocsAPIReference({ content }: Props): JSX.Element {
  const { colorMode } = useColorMode();
  const isLightMode = colorMode === 'light';

  return (
    <PageContainer>
      <Box
        py={{ base: 12, lg: 16, xl: 24 }}
        borderBottom="1px solid"
        borderColor="var(--borderColor)"
      >
        <Box maxW="container.lg" mx="auto" px={{ base: 6, lg: 10 }}>
          <Heading
            as="h1"
            color="var(--primaryTextColor)"
            size="h4"
            fontWeight="medium"
            mb={2}
          >
            {content.title}
          </Heading>
        </Box>
      </Box>

      <Stack
        spacing={12}
        py={12}
        px={{ base: 6, lg: 10 }}
        maxW="container.lg"
        mx="auto"
      >
        <SectionHeading thinText="Build with" boldText="Clerk SDKs" />

        <div className="grid auto-rows-fr grid-cols-1 gap-6 md:grid-cols-2 xl:grid-cols-3">
          {SDKs(isLightMode).map(
            ({ title, description, url, logoIcon, logoSize }) => (
              <Card
                key={title}
                title={title}
                description={description}
                url={url}
                logoIcon={logoIcon}
                logoSize={logoSize}
              />
            )
          )}
        </div>

        <Box pt={12}>
          <Support />
        </Box>
      </Stack>
    </PageContainer>
  );
}
